// ** Modules
import { useId } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
import { useTheme } from '@mui/material/styles';

// ** Components
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { AppContext } from '@/types/contextTypes';
import type { UploadFile, ComponentSharedLinkPage, ComponentSharedTextEditor } from '@/types/graphql';

export type ImageEntry = { src: string; alt?: string; videoUrl?: string };

export type CompetitionEntryDetail = {
	fullDescription?: ComponentSharedTextEditor;
	extraImages?: ImageEntry[];
	trainingCompany?: string;
	guild?: string;
	material?: string;
};

export type CompetitionCardModel = {
	id: string;
	name: string;
	description: string;
	cta?: ComponentSharedLinkPage;
	ranking?: 1 | 2 | 3;
	imageSrc?: string;
	imageAlt?: string;
	imageData?: UploadFile;
	host?: AppContext['host'];
	detail?: CompetitionEntryDetail;
};

export type CompetitionCardProps = CompetitionCardModel & {
	onClick?: () => void;
};

const RANKING_BORDER_COLORS: Record<1 | 2 | 3, string> = {
	1: '#D4A017',
	2: '#A0A0A0',
	3: '#8B6914'
};

const CompetitionCard = ({
	name,
	description,
	cta,
	ranking,
	imageSrc,
	imageAlt = '',
	imageData,
	host,
	onClick
}: CompetitionCardProps) => {
	const headingId = useId();
	const theme = useTheme();
	const hoverBorderColor = ranking ? RANKING_BORDER_COLORS[ranking] : null;
	const isClickable = Boolean(onClick);
	const ctaLabel = cta?.label || 'Mehr erfahren';
	const ctaHref = cta?.hrefExternal ? cta.hrefExternal : cta ? buildPathFromRelationLink(cta.href) : '#';

	return (
		<Box
			component="div"
			className="CompetitionCard-root"
			role={isClickable ? 'button' : undefined}
			tabIndex={isClickable ? 0 : undefined}
			aria-labelledby={headingId}
			onClick={onClick}
			onKeyDown={
				isClickable
					? (e: React.KeyboardEvent) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onClick?.();
							}
						}
					: undefined
			}
			sx={{
				width: '100%',
				maxWidth: 416,
				height: 560,
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: theme.palette.common.white,
				borderRadius: '8px',
				overflow: 'hidden',
				border: '2px solid transparent',
				cursor: isClickable ? 'pointer' : 'default',
				transition:
					'border-color 280ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 280ms cubic-bezier(0.22, 1, 0.36, 1)',
				...(isClickable && {
					'&:focusVisible': {
						outline: '2px solid',
						outlineColor: 'primary.main',
						outlineOffset: 2
					}
				}),
				...(hoverBorderColor
					? {
							'@media (hover: hover) and (pointer: fine)': {
								'&:hover': {
									borderColor: hoverBorderColor,
									boxShadow: `0 4px 20px ${hoverBorderColor}33`
								}
							},
							'&:focus-within': {
								borderColor: hoverBorderColor,
								boxShadow: `0 4px 20px ${hoverBorderColor}33`
							}
						}
					: isClickable
						? {
								'@media (hover: hover) and (pointer: fine)': {
									'&:hover': {
										boxShadow: '0 4px 16px rgba(0,0,0,0.10)'
									}
								},
								'&:focus-within': {
									boxShadow: '0 4px 16px rgba(0,0,0,0.10)'
								}
							}
						: {}),
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none'
				}
			}}
		>
			{/* Image */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					px: 8,
					pt: 8,
					backgroundColor: theme.palette.common.white,
					flexShrink: 0
				}}
			>
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						maxWidth: 248,
						aspectRatio: '1 / 1',
						overflow: 'hidden'
					}}
				>
					<StrapiImageRenderer
						src={imageSrc}
						imageData={imageData}
						host={host}
						fill
						sizes="248px"
						alt={imageAlt}
						imageFit="contain"
					/>
				</Box>
			</Box>

			<Divider sx={{ mt: 4, mx: 8, borderBottomWidth: '1px' }} />

			{/* Content area — grows to fill remaining card height */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					px: 8,
					pb: 6,
					flex: '1 1 auto',
					minHeight: 0
				}}
			>
				{/* Ranking (optional)*/}
				{ranking != null && (
					<Box
						sx={{
							mt: 8,
							display: 'flex',
							alignItems: 'center',
							gap: 2
						}}
					>
						<EmojiEventsOutlined
							aria-hidden="true"
							sx={{
								width: 21,
								height: 24,
								color: hoverBorderColor ?? theme.palette.primary.main
							}}
						/>
						<Typography
							variant="body-md"
							component="span"
							sx={{
								color: 'text.secondary',
								lineHeight: { xs: '1.5rem', md: '1.625rem' }
							}}
						>
							{`Platz ${ranking}`}
						</Typography>
					</Box>
				)}

				{/* Name */}
				<Typography
					id={headingId}
					variant="nav"
					component="h3"
					sx={{
						mt: 8,
						color: 'common.black',
						fontWeight: 400
					}}
				>
					{name}
				</Typography>

				{/* Description */}
				<Typography
					variant="body-md"
					component="p"
					sx={{
						mt: 2,
						color: 'text.secondary',
						fontSize: '1.125rem',
						lineHeight: '1.625rem',
						letterSpacing: '-0.009px',
						display: '-webkit-box',
						WebkitLineClamp: ranking != null ? 2 : 3,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						overflowWrap: 'break-word',
						hyphens: 'auto'
					}}
				>
					{description}
				</Typography>

				{/* CTA */}
				<Box sx={{ mt: 'auto', pt: 4 }}>
					<UnderlineHoverCta
						href={ctaHref}
						label={ctaLabel}
						color={theme.palette.text.secondary}
						typographyVariant="body-md"
						fontWeight={500}
						letterSpacing="-0.05px"
						activeOnParentHoverSelector=".CompetitionCard-root"
						underlineThickness="1px"
						underlineColor={hoverBorderColor ?? undefined}
						underlineBaseOpacity={0.2}
						underlineHoverOpacity={0.75}
						arrowShiftPx={2}
						paddingBottom="5px"
						underlineOffsetBottom={2}
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default CompetitionCard;
